/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { type GlobalConfig } from '@/features/globalConfig';
import { getAxios } from '@/lib/axios';
import { type AxiosInstance } from 'axios';
import { type QueryClient } from '@tanstack/react-query';
import type { TopReviewsResponse, TopReviewsData } from '../interface';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { useLocale } from '@/hooks/useLocale';
import type { Locale } from '@/types/i18n';
import { createQuery } from '@/utils/createReactQuery';
import { type GetTopReviewsUsingGetParamsModel } from '@/api/types/seo-data-types';
import environmentConfig from '@/config/environment';

type Options = {
  minimumReviewRating: number | undefined;
  pageType: 'ShopByMake' | 'ShopByModel' | 'ShopByYear' | undefined | null;
  partTypeId: string | undefined;
  vehicleMake: string | undefined;
  vehicleModel: string | undefined;
  vehicleYear: string | undefined;
};

type TopReviewsQueryOptions = {
  locale: Locale;
  minimumReviewRating: number | undefined;
  partTypeId: string | undefined;
  vehicleMake: string | undefined;
  vehicleModel: string | undefined;
  vehicleYear: string | undefined;
};

const URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-seo-data-bs/v2/reviews`;

const reviewsSelector = (data: TopReviewsResponse): TopReviewsData =>
  data.map(
    ({
      averageOverallRating,
      reviewSubmitTime,
      itemDescription,
      itemId,
      itemImageUrl,
      productDetailsPageUrl,
      totalReviewCount,
      ...rest
    }) => ({
      averageOverallRating: String(averageOverallRating),
      imageUrl: itemImageUrl,
      productPageUrl: productDetailsPageUrl,
      skuDescription: itemDescription,
      skuId: String(itemId),
      submissionTime: reviewSubmitTime,
      totalReviewCount: String(totalReviewCount),
      ...rest,
    }),
  );

const { useData: useProductTopReviews, prefetch: prefetchProductTopReviews } = createQuery<
  TopReviewsData,
  TopReviewsQueryOptions
>('topShelfReviews', async (options, axiosInstance) => getTopReviews(options, axiosInstance));

const getTopReviews = async (options: TopReviewsQueryOptions, axiosInstance?: AxiosInstance) => {
  const { locale, minimumReviewRating, partTypeId, vehicleMake, vehicleModel, vehicleYear } =
    options;
  const country = getCountryFromLocale(locale);
  const customerType = 'B2C';

  if (!vehicleMake) {
    throw new Error('Vehicle Make is required.');
  }

  if (!partTypeId) {
    throw new Error('Product ID is required.');
  }

  const params: GetTopReviewsUsingGetParamsModel = {
    country,
    customerType,
    minimumReviewRating,
    productId: partTypeId,
    vehicleMake,
    ...(vehicleModel && { vehicleModel }),
    ...(vehicleYear && { vehicleYear }),
  };

  const response = await getAxios(axiosInstance).get<TopReviewsResponse>(URL, {
    params,
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
  });
  return reviewsSelector(response.data);
};

const isEnabled = ({ pageType, vehicleMake, vehicleModel, partTypeId }: Options) => {
  const supportedPageTypes = new Set(['ShopByMake', 'ShopByModel', 'ShopByYear'] as const);
  const isValidPageType = pageType && supportedPageTypes.has(pageType);

  const isMissingRequiredData =
    !vehicleMake || !partTypeId || (pageType !== 'ShopByMake' && !vehicleModel);

  if (!isValidPageType || isMissingRequiredData) {
    return false;
  }

  return true;
};

export const useTopReviews = ({ enabled = true, ...options }: Options & { enabled?: boolean }) => {
  const locale = useLocale();

  return useProductTopReviews({
    enabled: enabled ? isEnabled(options) : false,
    locale,
    ...options,
  });
};

export const prefetchTopReviews = async (
  axiosInstance: AxiosInstance,
  queryClient: QueryClient,
  config: GlobalConfig,
  options: Options,
  locale: Locale,
) => {
  if (!isEnabled(options)) {
    return;
  }
  const opts: TopReviewsQueryOptions = {
    locale,
    ...options,
  };

  await prefetchProductTopReviews(queryClient, opts, axiosInstance);
};
